/* eslint-disable max-len */
import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ReferencePlateLine } from '@1po/1po-bff-fe-spec/generated/catalog/references/dh/model/Reference';
import { ReferenceStockTradingDataRequestDetail } from '@1po/1po-bff-fe-spec/generated/catalog/trading_data/request/GetReferencesStockRequest';
import { ReferenceTradingDataRequestDetail } from '@1po/1po-bff-fe-spec/generated/catalog/trading_data/request/GetReferenceTradingDataRequest';
import { RootState } from 'app/AppStore';
import { getReuseAvailabilitiesEnabled } from 'domains/appContext/AppContext.store';
import { getBasketOtherSection } from 'domains/basket/Basket.store';
import { getUniversalProducts } from 'domains/catalog/Catalog.store';
import {
  createIAMSupplierReferenceKey,
  parseIAMReferenceKeyNumber,
  parseIAMReferenceKeySupplier,
  TIRE,
} from 'domains/catalog/Catalog.types';
import { isDelegationSessionStatusLoading } from 'domains/delegation';
import {
  fetchCrossSellingReferencesRequestSaga,
  fetchEquivalentReferencesRequestSaga,
  fetchExadisEligibleReferencesRequestSaga,
  fetchIsDiscontinuedReferenceRequestSaga,
  fetchPlatesForReferenceRequestSaga,
  fetchReferencesDiscountsRequestSaga,
  fetchReferencesPriceRequestSaga,
  fetchReferencesRepairPriceRequestSaga,
  fetchReferencesStocksRequestSaga,
  fetchReuseAvailabilitiesRequestSaga,
  fetchReuseStocksRequestSaga,
  getDHReference,
  getDHReferences,
  getDHReferencesMap,
  getDiscountStatuses,
  getEquivalentReferences,
  getIAMReferencesMap,
  getPricesMap,
  getReferenceSources,
  getReferenceTypes,
  getRepairPricesMap,
  getReuseAvailabilitiesMap,
  getReuseStocksMap,
  getStocksMapV2,
  isDiscontinuedReference,
} from 'domains/references/References.store';
import {
  NON_MKTP,
  REUSE_AVAILABILITIES_ALLOWED_DEALER_TYPES,
  REUSE_NODE_IDS,
} from 'domains/references/References.types';
import { getTireSetParam } from 'domains/tires/Tire.store';
import { FOUND, getData, hasData, LOADING, NOT_FOUND, SearchData } from 'utils';
import { Country } from 'utils/i18n/Country';
import { getDealerType, getTradingProfile, getUserCountry } from '../user';
/* eslint-enable max-len */

const hasValidReferenceNumber = (referenceNumber: string | undefined): referenceNumber is string =>
  typeof referenceNumber === 'string' && referenceNumber.trim().length > 0;

export const useFetchCrossSellingReferences = (references: string[], vehicleKey: string | undefined): void => {
  const dispatch = useDispatch();
  const allLoadedReferences = useSelector((state: RootState) =>
    getDHReferences(state, { referenceNumbers: references, vehicleKey }),
  );
  const tradingProfile = useSelector(getTradingProfile);
  const isTradingProfileComplete = tradingProfile?.isComplete();
  const isDelegationLoading = useSelector(isDelegationSessionStatusLoading);
  const requestReferences = useMemo(() => {
    return references.filter((ref) => {
      const loadedRef = allLoadedReferences.find((r) => r.referenceNumber === ref);
      const loadedCrossSells = loadedRef?.crossSelling;
      return !hasData(loadedCrossSells) || loadedCrossSells.length === 0;
    });
  }, [references, allLoadedReferences]);

  const requestReferencesKey = useMemo(() => JSON.stringify(requestReferences.sort()), [requestReferences]);

  useEffect(() => {
    if (!isDelegationLoading && isTradingProfileComplete && requestReferences.length > 0) {
      dispatch(fetchCrossSellingReferencesRequestSaga({ references: requestReferences, vehicleKey }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, requestReferencesKey, isDelegationLoading, isTradingProfileComplete, vehicleKey]);
};

export const useFetchExadisEligibleReferences = (referenceNumbers: string[]): void => {
  const dispatch = useDispatch();
  const validReferenceNumbers = useMemo(() => referenceNumbers.filter(hasValidReferenceNumber), [referenceNumbers]);
  const requestReferencesKey = useMemo(
    () => JSON.stringify([...validReferenceNumbers].sort((a, b) => a.localeCompare(b))),
    [validReferenceNumbers],
  );

  useEffect(() => {
    if (validReferenceNumbers.length > 0) {
      dispatch(fetchExadisEligibleReferencesRequestSaga({ referenceNumbers: validReferenceNumbers }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, requestReferencesKey]);
};

export const useFetchPrices = (referenceNumbers: string[], vehicleKey?: string): void => {
  const dispatch = useDispatch();
  const tradingProfile = useSelector(getTradingProfile);
  const isTradingProfileComplete = tradingProfile?.isComplete();
  const isDelegationLoading = useSelector(isDelegationSessionStatusLoading);
  const validReferenceNumbers = referenceNumbers.filter(hasValidReferenceNumber);
  const plainReferenceNumbers = validReferenceNumbers.map(parseIAMReferenceKeyNumber);
  const prices = useSelector((state: RootState) => getPricesMap(state, plainReferenceNumbers));
  const universalProducts = useSelector((state: RootState) => getUniversalProducts(state, plainReferenceNumbers));
  const references = useSelector((state: RootState) =>
    getDHReferencesMap(state, { referenceNumbers: plainReferenceNumbers, vehicleKey }),
  );
  const iamReferences = useSelector((state: RootState) =>
    getIAMReferencesMap(state, { referenceNumbers: validReferenceNumbers, vehicleKey }),
  );

  useEffect(() => {
    const missingReferenceNumbers = validReferenceNumbers.filter(
      (referenceNumber) => prices.get(parseIAMReferenceKeyNumber(referenceNumber)) === undefined,
    );

    const referencesToFetch = missingReferenceNumbers.map((referenceKey) => {
      const referenceNumber = parseIAMReferenceKeyNumber(referenceKey);
      const iamReference = getData(iamReferences.get(referenceKey));
      return {
        referenceNumber,
        commercialFamily:
          getData(references.get(referenceNumber))?.familyCode ??
          getData(universalProducts.get(referenceNumber))?.familyCode ??
          '',
        supplierCode: iamReference?.supplierCode ?? parseIAMReferenceKeySupplier(referenceKey),
        origin: iamReference?.origin,
        referenceSource: iamReference?.referenceSource,
      };
    });

    if (!isDelegationLoading && isTradingProfileComplete && referencesToFetch.length > 0) {
      dispatch(
        fetchReferencesPriceRequestSaga({
          references: referencesToFetch,
          basketRequest: false,
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, isDelegationLoading, validReferenceNumbers]);
};

export const useFetchDiscounts = (references: ReferenceTradingDataRequestDetail[]): void => {
  const dispatch = useDispatch();
  const tradingProfile = useSelector(getTradingProfile);
  const isTradingProfileComplete = tradingProfile?.isComplete();
  const isDelegationLoading = useSelector(isDelegationSessionStatusLoading);
  const validReferences = references.filter((ref) => hasValidReferenceNumber(ref.referenceNumber));
  const referenceNumbers = validReferences.map((r) => r.referenceNumber);
  const discounts = useSelector((state: RootState) => getDiscountStatuses(state, referenceNumbers));

  useEffect(() => {
    const missingReferenceNumbers = Array.from(discounts.entries())
      .filter(([key, value]) => key !== undefined && value === undefined)
      .map(([key, _]) => key);

    const referencesToFetch = missingReferenceNumbers.map((r) => {
      return {
        referenceNumber: r,
        commercialFamily: getData(validReferences.find((ref) => ref.referenceNumber === r))?.commercialFamily ?? '',
      };
    });

    if (!isDelegationLoading && isTradingProfileComplete && referencesToFetch.length > 0) {
      dispatch(
        fetchReferencesDiscountsRequestSaga({
          references: referencesToFetch,
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, isDelegationLoading, referenceNumbers]);
};

export const useFetchReferenceStocks = (
  referenceNumber: string,
  stockRequestDetails?: ReferenceStockTradingDataRequestDetail,
  fetchQuantity?: number,
): void => {
  useFetchReferencesStocks(
    hasValidReferenceNumber(referenceNumber) ? [referenceNumber] : [],
    stockRequestDetails ? [stockRequestDetails] : [],
    fetchQuantity,
  );
};

export const useFetchReferencesStocks = (
  referenceNumbers: string[],
  stockRequestDetails?: ReferenceStockTradingDataRequestDetail[],
  fetchQuantity?: number,
): void => {
  const validReferenceNumbers = referenceNumbers.filter(hasValidReferenceNumber);
  const typesMap = useSelector((state: RootState) => getReferenceTypes(state, validReferenceNumbers));
  const sourceMap = useSelector((state: RootState) => getReferenceSources(state, validReferenceNumbers));
  const referencesToFetchStocks = validReferenceNumbers.map((r) => {
    const referenceNumber = parseIAMReferenceKeyNumber(r);
    const supplierCode = parseIAMReferenceKeySupplier(r);
    const stockRequestDetail = stockRequestDetails?.find((ref) =>
      supplierCode
        ? createIAMSupplierReferenceKey(ref.referenceNumber, ref.supplierCode) ===
          createIAMSupplierReferenceKey(referenceNumber, supplierCode)
        : ref.referenceNumber === referenceNumber,
    );
    return {
      referenceNumber,
      type: stockRequestDetail?.type ?? typesMap.get(referenceNumber) ?? 'STANDARD',
      quantity: fetchQuantity ?? 1,
      origin: stockRequestDetail?.origin,
      supplierCode: stockRequestDetail?.supplierCode ?? supplierCode,
      referenceSource: stockRequestDetail?.referenceSource ?? sourceMap.get(referenceNumber) ?? 'STANDARD',
      isPrimaryStockRequest: stockRequestDetail?.isPrimaryStockRequest ?? false,
      isCertasReference:
        stockRequestDetails?.find((ref) => ref.referenceNumber === referenceNumber)?.isCertasReference ?? false,
    };
  });
  useFetchStocks(referencesToFetchStocks);
};

export const useFetchTireStocks = (referenceNumbers: string[]): void => {
  const setParam = useSelector(getTireSetParam);
  const otherSection = useSelector(getBasketOtherSection);
  const currentBasketReferencesMap = otherSection?.references?.reduce(
    (acc, next) => acc.set(next.referenceNumber, next.quantity),
    new Map<string, number>(),
  );
  const stocks = useSelector((state: RootState) => getStocksMapV2(state, referenceNumbers));

  const referencesToFetchStocks = referenceNumbers
    .map((r) => {
      const currQuantity = currentBasketReferencesMap.get(r);
      return {
        referenceNumber: r,
        type: 'TIRE',
        quantity: currQuantity ?? setParam ?? 1,
        origin: undefined,
        supplierCode: undefined,
        referenceSource: 'STANDARD',
        isPrimaryStockRequest: false,
      } as ReferenceStockTradingDataRequestDetail;
    })
    .filter((ref) => {
      const stock = stocks.get(ref.referenceNumber);
      return (
        stock === undefined ||
        (stock?.data !== undefined && stock?.data?.get(NON_MKTP)?.confirmedQuantity !== ref.quantity)
      );
    });
  useFetchStocks(referencesToFetchStocks, true);
};

export const useFetchReuseStocks = (referenceNumbers: string[], nodeId?: string): void => {
  const dispatch = useDispatch();
  const userCountry = useSelector(getUserCountry);
  const isDelegationLoading = useSelector(isDelegationSessionStatusLoading);
  const referenceTypesMap = useSelector((state: RootState) => getReferenceTypes(state, referenceNumbers));
  const reuseStocks = useSelector((state: RootState) => getReuseStocksMap(state, referenceNumbers));

  useEffect(() => {
    if (isDelegationLoading) {
      return;
    }
    if (userCountry !== Country.FR.key || referenceNumbers.length < 1 || (nodeId && !REUSE_NODE_IDS.includes(nodeId))) {
      return;
    }

    const diff = referenceNumbers.filter((ref) => {
      const curr = reuseStocks.get(ref);
      const refType = referenceTypesMap.get(ref);
      return refType !== TIRE && curr === undefined;
    });

    if (diff.length < 1) return;
    dispatch(
      fetchReuseStocksRequestSaga({
        referenceNumbers: diff,
        nodeId,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, isDelegationLoading, referenceNumbers, referenceTypesMap, nodeId]);
};

export const useFetchReuseAvailabilities = (referenceNumbers: string[]): void => {
  const dispatch = useDispatch();
  const userCountry = useSelector(getUserCountry);
  const dealerType = useSelector(getDealerType);
  const reuseAvailabilitiesEnabled = useSelector(getReuseAvailabilitiesEnabled);
  const isDelegationLoading = useSelector(isDelegationSessionStatusLoading);
  const referenceTypesMap = useSelector((state: RootState) => getReferenceTypes(state, referenceNumbers));
  const reuseAvailabilities = useSelector((state: RootState) => getReuseAvailabilitiesMap(state, referenceNumbers));

  useEffect(() => {
    if (isDelegationLoading) {
      return;
    }
    if (
      !reuseAvailabilitiesEnabled ||
      userCountry !== Country.FR.key ||
      !dealerType ||
      !REUSE_AVAILABILITIES_ALLOWED_DEALER_TYPES.includes(dealerType) ||
      referenceNumbers.length < 1
    ) {
      return;
    }

    const diff = referenceNumbers.filter((ref) => {
      const curr = reuseAvailabilities.get(ref);
      const refType = referenceTypesMap.get(ref);
      return refType !== TIRE && curr === undefined;
    });

    if (diff.length < 1) return;
    dispatch(
      fetchReuseAvailabilitiesRequestSaga({
        referenceNumbers: diff,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispatch,
    isDelegationLoading,
    reuseAvailabilitiesEnabled,
    userCountry,
    dealerType,
    referenceNumbers,
    referenceTypesMap,
  ]);
};

export const useFetchEquivalentReferences = (
  reference: string,
  isDiscontinued: boolean,
): SearchData<string[]> | undefined => {
  const dispatch = useDispatch();
  const isDelegationLoading = useSelector(isDelegationSessionStatusLoading);
  const equivalentReferences = useSelector((state: RootState) => getEquivalentReferences(state, reference));

  useEffect(() => {
    if (isDelegationLoading) {
      return;
    }
    if (equivalentReferences?.searchStatus === undefined && isDiscontinued) {
      dispatch(fetchEquivalentReferencesRequestSaga({ reference }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, reference, isDiscontinued, isDelegationLoading]);
  return equivalentReferences;
};

export const useFetchStocks = (
  requestStockDetails: ReferenceStockTradingDataRequestDetail[],
  fetchEvenIfExists = false,
): void => {
  const dispatch = useDispatch();
  const tradingProfile = useSelector(getTradingProfile);
  const isTradingProfileComplete = tradingProfile?.isComplete();
  const isDelegationLoading = useSelector(isDelegationSessionStatusLoading);
  const referenceNumbers = requestStockDetails.map((r) => r.referenceNumber);
  const stocks = useSelector((state: RootState) => getStocksMapV2(state, referenceNumbers));
  useEffect(() => {
    const diff = requestStockDetails.filter((detail) => {
      const curr = stocks.get(detail.referenceNumber);
      if (detail.isPrimaryStockRequest) {
        return true;
      }
      return (
        curr === undefined ||
        (curr.data && curr.data.get(detail.sellerNumber ?? NON_MKTP)?.confirmedQuantity !== detail.quantity)
      );
    });

    if (!isDelegationLoading && isTradingProfileComplete && diff.length > 0) {
      dispatch(
        fetchReferencesStocksRequestSaga({
          data: diff,
          fetchEvenIfExists,
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, isDelegationLoading, JSON.stringify(requestStockDetails)]);
};

export const useFetchRepairPrices = (referenceNumbers: string[], vehicleKey?: string): void => {
  const dispatch = useDispatch();
  const tradingProfile = useSelector(getTradingProfile);
  const isTradingProfileComplete = tradingProfile?.isComplete();
  const isDelegationLoading = useSelector(isDelegationSessionStatusLoading);
  const prices = useSelector((state: RootState) => getRepairPricesMap(state, referenceNumbers));
  const references = useSelector((state: RootState) => getDHReferencesMap(state, { referenceNumbers, vehicleKey }));

  useEffect(() => {
    const missingReferenceNumbers = Array.from(prices.entries())
      .filter(([_, value]) => value === undefined)
      .map(([key, _]) => key);

    const referencesToFetch = missingReferenceNumbers.map((r) => {
      return { referenceNumber: r, commercialFamily: getData(references.get(r))?.familyCode ?? '' };
    });

    if (!isDelegationLoading && isTradingProfileComplete && missingReferenceNumbers.length > 0) {
      dispatch(fetchReferencesRepairPriceRequestSaga({ references: referencesToFetch }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, isDelegationLoading, isTradingProfileComplete, referenceNumbers]);
};

export const useFetchFullTradingData = (referenceNumber: string, vehicleKey?: string): void => {
  useFetchPrices([referenceNumber], vehicleKey);
  useFetchReferencesStocks([referenceNumber]);
};

export const useFetchFullTradingDatas = (
  referenceNumbers: string[],
  vehicleKey?: string,
  stockRequestDetails?: ReferenceStockTradingDataRequestDetail[],
): void => {
  useFetchPrices(referenceNumbers, vehicleKey);
  useFetchReferencesStocks(referenceNumbers, stockRequestDetails);
};

export const useFetchPlatesForReference = (
  referenceNumber: string,
  vehicleKey: string | undefined,
): SearchData<ReferencePlateLine[]> => {
  const dispatch = useDispatch();
  const reference = useSelector((state: RootState) => getDHReference(state, { vehicleKey, referenceNumber }));
  const ref = getData(reference);

  useEffect(() => {
    if (vehicleKey && ref && ref?.referencePlateLinesStatus === undefined) {
      dispatch(
        fetchPlatesForReferenceRequestSaga({
          referenceNumber,
          vehicleKey,
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referenceNumber, ref?.referencePlateLinesStatus, vehicleKey]);
  return {
    searchStatus: ref?.referencePlateLinesStatus,
    data: ref?.referencePlateLines,
  };
};

export const useFetchDiscontinuedReferences = (referenceNumber: string): SearchData<boolean> => {
  const dispatch = useDispatch();
  const isDelegationLoading = useSelector(isDelegationSessionStatusLoading);
  const discontinuedReference = useSelector((state: RootState) => isDiscontinuedReference(state, referenceNumber));

  useEffect(() => {
    if (isDelegationLoading) {
      return;
    }
    if (
      discontinuedReference?.searchStatus === FOUND ||
      discontinuedReference?.searchStatus === LOADING ||
      discontinuedReference?.searchStatus === NOT_FOUND
    ) {
      return;
    }

    dispatch(fetchIsDiscontinuedReferenceRequestSaga({ referenceNumber }));
  }, [dispatch, discontinuedReference?.searchStatus, referenceNumber, isDelegationLoading]);

  return {
    searchStatus: discontinuedReference?.searchStatus,
    data: discontinuedReference?.data ?? false,
  };
};
